import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { chW, chH } from "../../design/ui";
import { useApp } from "../../design/AppContext";
import { apiGet, currentAccount } from "../../utils/api";
import { EmptyState, SearchField, StatusChip } from "../../components/mui";
import { useListPage } from "../../components/design/ListPager";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

/**
 * 個人績效 — 1:1 對應 DESIGN 稿 L346-L382（markup）與 L1161-L1163、L1328-L1335（myPerf / ppTrend / peer / badges / ppDetail）。
 *
 * 設計稿全部是寫死的種子資料，這裡改接：
 *   GET_PerformanceReport → 圓環、統計卡、完成明細
 *   GET_PerformanceTeam   → 同儕對照的團隊平均、成員下拉
 *   /Performance/Report（直接呼叫）→ 近 6 個月完成率趨勢（後端沒有 trend 端點，逐月取一次）
 */

/** 後端可能回小數或字串，統一轉成設計稿顯示的整數 */
const num = (v) => Math.round(Number(v) || 0);

/**
 * 模糊搜尋：把該列所有可見文字併成一串，關鍵字以空白分隔且全部命中（AND）才算符合。
 * 不分大小寫；數字欄位（分數）也轉成字串一起比對，使用者才能直接打「100」找滿分工項。
 */
function matchQ(q, fields) {
  const kws = String(q || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!kws.length) return true;
  const hay = (fields || [])
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(" ")
    .toLowerCase();
  return kws.every((k) => hay.includes(k));
}

/** 成員超過這個人數，下拉才會長到需要過濾；人少時維持設計稿原本的單一下拉不加雜訊 */
const MEMBER_FILTER_MIN = 5;

/**
 * 準時判定與後端 PerformanceService 一致：完成時間 ≤ 截止日 +1 天寬限。
 * 沒有截止日的工項後端不計入準時率，這裡視為準時（不會被標成逾期）。
 */
function isOnTime(t) {
  const due = _.get(t, "due_date", null) ?? _.get(t, "dueDate", null);
  // 依 Git 紀錄補登的截止日（＝完成日）後端不計入準時率，這裡同樣當成沒有截止日
  if (!due || _.get(t, "is_due_backfilled", false)) return true;
  const doneAt = _.get(t, "completed_at", null) ?? _.get(t, "completedAt", null);
  if (!doneAt) return false;
  const d = new Date(doneAt);
  const dd = new Date(due);
  if (Number.isNaN(d.getTime()) || Number.isNaN(dd.getTime())) return true;
  return d.getTime() <= dd.getTime() + 86400000;
}

/** 徽章成就 — 設計稿寫死的四張卡，後端尚無徽章 API，先照設計稿呈現 */
const BADGE_SEED = [
  { label: "準時達人", desc: "連續 3 個月準時率 ≥ 90%", c: "#0e7c66" },
  { label: "零缺陷", desc: "本季無重開缺陷", c: "#2563eb" },
  { label: "審核王", desc: "完成 20+ 次 Code Review", c: "#7c5cff" },
  { label: "交付穩定", desc: "速度標準差 < 10%", c: "#dc6803" },
];

function App(props) {
  const { team, report, GET_PerformanceTeam, GET_PerformanceReport } = props;
  const [searchParams] = useSearchParams();
  const { userInfo } = useApp();

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(Number(searchParams.get("year")) || today.getFullYear());
  const [month, setMonth] = useState(Number(searchParams.get("month")) || today.getMonth() + 1);
  const [account, setAccount] = useState(searchParams.get("account") || "");
  const [trend, setTrend] = useState([]);
  /* 成員下拉的過濾字串（人多時才用得到）與完成明細的模糊搜尋字串 */
  const [memberQ, setMemberQ] = useState("");
  const [detailQ, setDetailQ] = useState("");

  useEffect(() => {
    GET_PerformanceTeam({ year, month });
  }, [GET_PerformanceTeam, year, month]);

  // 目前登入者（token 解出來的，第一個 render 就有值，不必等 redux）
  const me = useMemo(() => currentAccount(), []);
  const myName = _.get(userInfo, "name", "");

  // _.get 的預設值每次都是新陣列，memo 起來才不會讓下游 useMemo/useEffect 每次 render 都重跑
  const members = useMemo(() => {
    const raw = _.get(team, "members", []);
    return Array.isArray(raw) ? raw : [];
  }, [team]);

  /**
   * 下拉成員：以團隊報表為主。
   * 團隊 API 只回 RD 角色成員，非 RD 的使用者（PM/PO…）與 ?account= 指定的人都不在名單裡，
   * 不補進去的話下拉會顯示不出他們、選取也會被下面的自動選定改掉，所以兩者一律補上。
   */
  const qAccount = searchParams.get("account") || "";
  const memberOpts = useMemo(() => {
    const opts = members
      .map((m) => ({ account: _.get(m, "account", ""), name: _.get(m, "name", "") || _.get(m, "account", "") }))
      .filter((m) => m.account);
    const ensure = (acc, name) => {
      if (!acc || opts.some((o) => o.account === acc)) return;
      opts.unshift({ account: acc, name: name || acc });
    };
    // 月報本身會回 name，拿得到就顯示真實姓名，拿不到先用帳號墊著
    ensure(qAccount, _.get(report, "account", "") === qAccount ? _.get(report, "name", "") : "");
    ensure(me, myName);
    return opts;
  }, [members, me, myName, qAccount, report]);

  /**
   * 下拉實際顯示的選項：成員一多（demo 資料 10+ 人）純下拉很難找人，超過門檻才開過濾。
   * 自動選定的 effect 仍看未過濾的 memberOpts，所以打字過濾不會影響已選定的人。
   */
  const showMemberFilter = memberOpts.length > MEMBER_FILTER_MIN;
  const memberFiltered = useMemo(() => {
    if (!showMemberFilter) return { opts: memberOpts, hits: memberOpts.length };
    const kept = memberOpts.filter((m) => matchQ(memberQ, [m.name, m.account]));
    const hits = kept.length;
    // 目前選定的人一定要留在選項裡，否則 select 的 value 找不到對應 option 會顯示成空白；
    // hits 另外記，才能在「補回選定者」之後仍判斷得出關鍵字其實沒命中任何人
    if (account && !kept.some((o) => o.account === account)) {
      const cur = memberOpts.find((o) => o.account === account);
      if (cur) kept.unshift(cur);
    }
    return { opts: kept, hits };
  }, [memberOpts, memberQ, account, showMemberFilter]);
  const memberOptsShown = memberFiltered.opts;

  // 設計稿的下拉沒有「請選擇」空選項，所以清單一到就自動選定（優先選自己）
  useEffect(() => {
    if (!memberOpts.length) return;
    if (account && memberOpts.some((o) => o.account === account)) return;
    const hit = memberOpts.find((o) => o.account === me);
    setAccount(hit ? hit.account : memberOpts[0].account);
  }, [memberOpts, account, me]);

  useEffect(() => {
    if (!account) return;
    GET_PerformanceReport({ year, month, account });
  }, [GET_PerformanceReport, account, year, month]);

  /** 趨勢圖的 6 個月（含當月）；標籤與抓取共用同一份，避免軸與資料錯位 */
  const trendMonths = useMemo(() => {
    const arr = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(year, month - 1 - i, 1);
      arr.push({ y: d.getFullYear(), m: d.getMonth() + 1 });
    }
    return arr;
  }, [year, month]);

  useEffect(() => {
    if (!account) {
      setTrend([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      trendMonths.map(async (p) => {
        // 後端未提供趨勢端點，逐月取月報；任一月失敗（含 404）就當 0，不讓整張圖掛掉
        try {
          const r = await apiGet("/Performance/Report", { year: p.y, month: p.m, account });
          return num(_.get(r, "kpi.completion_rate", 0));
        } catch (e) {
          return 0;
        }
      })
    ).then((vals) => {
      if (!cancelled) setTrend(vals);
    });
    return () => {
      cancelled = true;
    };
  }, [account, trendMonths]);

  /** 用 Date 位移避免自己處理跨年進位 */
  const shiftMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const kpi = _.get(report, "kpi", {}) || {};
  /* 後端在本月沒有任何工項時會把所有分數歸零並回 has_data=false；
     舊版沒有這個欄位，退而用總工項數判斷，避免升級前後畫面不一致 */
  const hasData = _.get(report, "has_data", _.get(report, "total_tasks", 0) > 0);
  /* 後端沒回 / 回 null / 回成非陣列（例如 {} 或 { data_list: [] } 這種外層形狀）都要當成空清單，不能讓下面的 filter 炸掉 */
  const tasks = useMemo(() => {
    const raw = _.get(report, "tasks", []);
    return Array.isArray(raw) ? raw : [];
  }, [report]);
  const doneTasks = useMemo(() => tasks.filter((t) => Number(_.get(t, "status", -1)) === 3), [tasks]);

  /* ---------- 綜合績效圓環 ---------- */
  const myPerf = [
    { label: "完成率", val: `${num(kpi.completion_rate)}%`, pct: num(kpi.completion_rate), color: "#0e7c66" },
    { label: "準時率", val: `${num(kpi.on_time_rate)}%`, pct: num(kpi.on_time_rate), color: "#2563eb" },
    { label: "紮實度", val: `${num(kpi.solidity_score)}%`, pct: num(kpi.solidity_score), color: "#7c5cff" },
    { label: "品質分", val: `${num(kpi.quality_score)}`, pct: num(kpi.quality_score), color: "#dc6803" },
  ].map((p) => ({ ...p, deg: `${p.pct * 3.6}deg` }));

  /* ---------- 小統計卡 ---------- */
  const myPerfStats = [
    { val: num(kpi.project_count), label: "參與專案" },
    { val: num(_.get(report, "total_tasks", 0)), label: "總工項" },
    { val: num(_.get(report, "completed_tasks", 0)), label: "已完成" },
    { val: doneTasks.filter(isOnTime).length, label: "準時完成" },
  ];

  /* ---------- 完成率趨勢 ---------- */
  const ppTrend = trend.length === trendMonths.length ? trend : trendMonths.map(() => 0);
  const ppMonths = trendMonths.map((p) => `${p.m} 月`);
  const ppN = ppTrend.length;
  const ppX = (i) => (ppN > 1 ? i * (chW / (ppN - 1)) : 0);
  // 設計稿的 y 軸只映射 60~100 分；真實資料可能落在區間外，夾在畫布內避免線畫到看不見的地方
  const ppY = (v) => Math.max(12, Math.min(chH - 18, chH - 18 - ((v - 60) / 40) * (chH - 30)));
  const ppLine = ppTrend.map((v, i) => `${ppX(i).toFixed(0)},${ppY(v).toFixed(0)}`).join(" ");
  const ppDots = ppTrend.map((v, i) => ({ cx: ppX(i).toFixed(0), cy: ppY(v).toFixed(0) }));

  /* ---------- 同儕對照 ---------- */
  const teamAvg = (path) =>
    members.length
      ? Math.round(members.reduce((s, m) => s + (Number(_.get(m, path, 0)) || 0), 0) / members.length)
      : 0;
  const peer = [
    { label: "完成率", me: num(kpi.completion_rate), team: teamAvg("kpi.completion_rate") },
    { label: "準時率", me: num(kpi.on_time_rate), team: teamAvg("kpi.on_time_rate") },
    { label: "紮實度", me: num(kpi.solidity_score), team: teamAvg("kpi.solidity_score") },
    { label: "品質分", me: num(kpi.quality_score), team: teamAvg("kpi.quality_score") },
  ].map((p) => ({
    ...p,
    meW: `${p.me}%`,
    teamW: `${p.team}%`,
    up: p.me >= p.team,
    diffText: `${p.me >= p.team ? "+" : ""}${p.me - p.team}`,
  }));

  /* ---------- 徽章 ---------- */
  const badges = BADGE_SEED.map((b) => ({
    ...b,
    ic: b.label[0],
  }));

  /* ---------- 完成明細 ---------- */
  /* 月份／成員沒變就不重算：工項多達千筆時，每打一個字都重建整份陣列太浪費 */
  const ppDetail = useMemo(
    () =>
      doneTasks.map((t, i) => {
        const onTime = isOnTime(t);
        const rawScore = _.get(t, "score", null) ?? _.get(t, "Score", null);
        // 績效 API 的工項投影沒有 issue_key，退回顯示流水號
        const code = _.get(t, "issue_key", "") || _.get(t, "issueKey", "") || `#${_.get(t, "id", "")}`;
        const name = _.get(t, "title", "") || "";
        // 後端尚未提供單項分數，沿用設計稿的準時 100 / 逾期 80
        const score = rawScore == null ? (onTime ? 100 : 80) : num(rawScore);
        const onTimeText = onTime ? "準時" : "逾期";
        return {
          key: _.get(t, "id", "") || `d-${i}`,
          code,
          name,
          onTime,
          score,
          onTimeText,
          /* 這一列所有「看得到的文字」先併好、先轉小寫，搜尋時只做 includes（同 Risk/Index.js 的作法） */
          hay: `${code} ${name} ${onTimeText} ${score}`.toLowerCase(),
        };
      }),
    [doneTasks]
  );

  /* 模糊搜尋比對整列可見文字（代碼、名稱、準時與否、分數），多關鍵字 AND */
  const ppDetailShown = useMemo(() => ppDetail.filter((d) => matchQ(detailQ, [d.hay])), [ppDetail, detailQ]);
  /*
   * 分頁吃「搜尋後」的陣列，總筆數才會反映篩選結果；
   * 第三個參數是重設鍵——搜尋字串／成員／月份一變就回到第 1 頁，
   * 否則停在第 3 頁時打字搜尋會停在第 3 頁，前面的命中結果全被藏住。
   */
  const detailPage = useListPage(ppDetailShown, 10, `${account}|${year}-${month}|${detailQ}`);

  return (
    <Box className="pg" sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        {showMemberFilter && (
          <SearchField
            value={memberQ}
            onChange={(e) => setMemberQ(e.target.value)}
            placeholder="過濾成員…"
            sx={{ width: 180 }}
          />
        )}
        <TextField
          select
          slotProps={{ select: { native: true }, htmlInput: { "aria-label": "成員" } }}
          value={account}
          onChange={(e) => setAccount(e.target.value)}
        >
          {memberOptsShown.map((m) => (
            <option key={m.account} value={m.account}>
              {m.name}
            </option>
          ))}
        </TextField>
        {showMemberFilter && memberQ.trim() !== "" && memberFiltered.hits === 0 && (
          <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
            查無成員
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Tooltip title="上個月">
            <IconButton aria-label="上個月" onClick={() => shiftMonth(-1)}>
              <MdChevronLeft aria-hidden="true" />
            </IconButton>
          </Tooltip>
          <Typography variant="h3" component="div">{`${year} 年 ${month} 月`}</Typography>
          <Tooltip title="下個月">
            <IconButton aria-label="下個月" onClick={() => shiftMonth(1)}>
              <MdChevronRight aria-hidden="true" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.25} useFlexGap sx={{ alignItems: "center", mb: 2.25, flexWrap: "wrap" }}>
            <Typography variant="h3" component="h2">
              綜合績效
            </Typography>
            {/*
              沒有工項就沒有母數可以算分。後端這種情況一律回 0，
              但畫面上四個 0 看起來像「表現很差」而不是「沒有資料」，所以要明講。
            */}
            {!hasData && <StatusChip kind="closed">本月無工項資料，分數不計</StatusChip>}
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 2.5 }}>
            {myPerf.map((p) => (
              <Box key={p.label} sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    position: "relative",
                    width: 92,
                    height: 92,
                    mx: "auto",
                    borderRadius: "50%",
                    background: `conic-gradient(${p.color} ${p.deg},var(--line) 0)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "var(--t-section)",
                      fontWeight: 900,
                    }}
                  >
                    {p.val}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 1.25 }}>
                  {p.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 1.75 }}>
        {myPerfStats.map((s) => (
          <Card key={s.label}>
            <CardContent>
              <Typography variant="h2" component="div" sx={{ fontWeight: 900 }}>
                {s.val}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5 }}>
                {s.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="h3" component="h2" sx={{ mb: 1.75 }}>
              完成率趨勢{" "}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                近 6 個月
              </Typography>
            </Typography>
            <Box component="svg" viewBox={`0 0 ${chW} ${chH}`} sx={{ width: "100%", height: "auto" }}>
              <polyline points={ppLine} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
              {ppDots.map((d, i) => (
                <circle key={`dot-${trendMonths[i].y}-${trendMonths[i].m}`} cx={d.cx} cy={d.cy} r="3.5" fill="var(--accent)" />
              ))}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
              {ppMonths.map((m, i) => (
                <Typography
                  key={`m-${trendMonths[i].y}-${trendMonths[i].m}`}
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {m}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h3" component="h2" sx={{ mb: 1.75 }}>
              同儕對照{" "}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                我 vs 團隊平均
              </Typography>
            </Typography>
            <Stack direction="row" spacing={1.75} sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary" component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.625, fontWeight: 600 }}>
                <Box component="span" sx={{ width: 12, height: 7, borderRadius: "var(--radius-xs)", bgcolor: "var(--accent)" }} />我
              </Typography>
              <Typography variant="body2" color="text.secondary" component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.625, fontWeight: 600 }}>
                <Box component="span" sx={{ width: 12, height: 7, borderRadius: "var(--radius-xs)", bgcolor: "var(--faint)" }} />團隊平均
              </Typography>
            </Stack>
            {peer.map((p) => (
              <Box key={p.label} sx={{ mb: 1.625 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.625 }}>
                  <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {p.label}
                  </Typography>
                  <Typography variant="body2" component="span">
                    <Box component="b" sx={{ fontWeight: 800 }}>{p.me}</Box>{" "}
                    <Box component="span" sx={{ fontWeight: 800, color: p.up ? "success.main" : "error.main" }}>
                      {p.diffText}
                    </Box>
                  </Typography>
                </Box>
                <Box sx={{ position: "relative", height: 8, bgcolor: "var(--line)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
                  <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: p.teamW, bgcolor: "var(--faint)" }} />
                  <Box sx={{ position: "absolute", left: 0, top: 0, height: "100%", width: p.meW, bgcolor: "var(--accent)", opacity: 0.85 }} />
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h3" component="h2" sx={{ mb: 1.75 }}>
            徽章成就
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 1.75 }}>
            {badges.map((b) => (
              <Box key={b.label} sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: "action.hover", borderRadius: "var(--radius-sm)", px: 1.875, py: 1.625 }}>
                <Box
                  component="span"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    bgcolor: `${b.c}1f`,
                    color: b.c,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "var(--t-body)",
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  {b.ic}
                </Box>
                <div>
                  <Typography sx={{ fontWeight: 800 }}>{b.label}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {b.desc}
                  </Typography>
                </div>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ overflow: "hidden" }}>
        {/* 標題列：左側標題與筆數，右側模糊搜尋 */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h3" component="h2">
            完成明細
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
            {detailQ.trim() ? `共 ${ppDetailShown.length} / ${ppDetail.length} 筆` : `共 ${ppDetail.length} 筆`}
          </Typography>
          <SearchField
            value={detailQ}
            onChange={(e) => setDetailQ(e.target.value)}
            placeholder="搜尋工項、代碼、準時、分數…"
            sx={{ ml: "auto", flex: 1, minWidth: 180, maxWidth: 280 }}
          />
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 460 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell>工項</TableCell>
                <TableCell align="center">
                  準時
                </TableCell>
                <TableCell align="right">
                  分數
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detailPage.pageItems.map((d) => (
                <TableRow key={d.key}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {d.code}
                    </Typography>{" "}
                    {d.name}
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip kind={d.onTime ? "done" : "warn"} size="small">
                      {d.onTimeText}
                    </StatusChip>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: "var(--t-section)", fontWeight: 900, color: "primary.main", fontVariantNumeric: "tabular-nums" }}
                  >
                    {d.score}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* 兩種空狀態要分開：本來就沒資料 vs 搜尋沒命中，後者要提示改關鍵字 */}
        {ppDetail.length === 0 && <EmptyState dense text="此月份無工項資料" />}
        {ppDetail.length > 0 && ppDetailShown.length === 0 && (
          <EmptyState dense text="查無符合的工項，請調整搜尋關鍵字" />
        )}
        {detailPage.pager}
      </Card>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  team: _.get(state, "Performance.Team", {}),
  report: _.get(state, "Performance.Report", {}),
});

const mapDispatchToProps = (dispatch) => ({
  GET_PerformanceTeam(payload, callback, loading) {
    dispatch({ type: "GET_PerformanceTeam", payload, callback, loading });
  },
  GET_PerformanceReport(payload, callback, loading) {
    dispatch({ type: "GET_PerformanceReport", payload, callback, loading });
  },
});

App.propTypes = {
  team: PropTypes.object,
  report: PropTypes.object,
  GET_PerformanceTeam: PropTypes.func,
  GET_PerformanceReport: PropTypes.func,
};

App.defaultProps = {
  team: {},
  report: {},
  GET_PerformanceTeam: () => {},
  GET_PerformanceReport: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
